import _ from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"

import {
  gtmPushDataLayerSearchKeyword,
  gtmPushDataLayerCategoryQuickSearch,
} from "utils/googletagmanager"
import { listItemPropsParserFactory } from "utils/feature"

import config from "config"

import { useSponsoredSuggestedBrand } from "hooks/sponsored-content/useSponsoredSuggestedBrand"
import { useSponsoredSearchResult } from "hooks/sponsored-content/useSponsoredSearchResult"
import { useInfiniteGetSearchResult } from "hooks/feature/useSearchFeatures/useInfiniteGetSearchResult"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"

import { GTM_NO_RESULT_VALUE, DEFAULT_VENUE_FILTER, Q_QUERY } from "constant"
import { useSearchParams } from "react-router-dom"

export const useSearchContainer = ({
  showOnscreenKeyboard: showOnscreenKeyboardProp = undefined,
} = {}) => {
  const { t } = useTranslation({ defaultValue: null })
  const navigate = useNavigate()
  const {
    search,
    isSearchReady,
    getSuggestedSearchItems,
    isMultiOrdinalVenue,
    occupants: venueOccupant,
    isMultiVenueProject,
    keyboardConfig,
    pageConfig,
  } = useVenue()

  const [searchParams, setSearchParams] = useSearchParams()
  const { openSearchState, openSearch, closeSearch } = useAppUI()
  const { searchResults, searchResultConfig } = useSponsoredSearchResult()
  const { suggestedBrands } = useSponsoredSuggestedBrand()
  const [venueFilter, setVenueFilter] = useState<string>(DEFAULT_VENUE_FILTER)

  const sanitizedVenueFilter =
    venueFilter === DEFAULT_VENUE_FILTER ? null : venueFilter

  const searchValue = useMemo(() => searchParams.get(Q_QUERY), [searchParams])

  const {
    data,
    hasNextPage,
    // isFetched,
    fetchNextPage,
  } = useInfiniteGetSearchResult({
    queryKey: ["search", searchValue],
    pagination: {
      perPage: 25,
    },
    filter: {
      q: searchValue,
      venueId: sanitizedVenueFilter,
    },
    gcTime: 0,
    searchFn: search,
    enabled: isSearchReady,
  })

  const onReachEndScroll = useCallback(
    (inView) => {
      if (inView && hasNextPage) fetchNextPage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchNextPage, hasNextPage, data]
  )

  const { endScrollRef } = useInfiniteScroll({
    onReachEndScroll,
  })

  const searchInputRef = useRef()
  const keyboardRef = useRef()

  // To open the On-Screen Keyboard
  const device = config("device")
  const isKiosk = /^kiosk/.test(device)
  const showOnscreenKeyboard = _.isBoolean(showOnscreenKeyboardProp)
    ? showOnscreenKeyboardProp
    : isKiosk

  const setSearchValue = useCallback(
    (value) => {
      setSearchParams(
        (prev) => {
          if (value) {
            prev.set(Q_QUERY, value)
          } else {
            prev.delete(Q_QUERY)
          }
          return prev
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // Implement Search With Custom Debounce function
  // `setSearchValue` depends on `setSearchParams`, which react-router recreates
  // on every query-string change. Depending on it here would rebuild the
  // debounce whenever any other writer touches the URL (a filter bar, or the
  // map's moveend/zoomend tracking) and the cleanup below would then cancel the
  // pending keystroke — silently dropping the last character typed. Keep the
  // debounced function stable and read the latest writer through a ref.
  const setSearchValueRef = useRef(setSearchValue)
  setSearchValueRef.current = setSearchValue

  const debounceSearchOnChange = useMemo(
    () =>
      _.debounce((value) => {
        setSearchValueRef.current(value)
      }, 400), // Debounce for 400 milliseconds
    []
  )

  // Search Result
  const resultParser = listItemPropsParserFactory({
    t,
    hideLevel: !isMultiOrdinalVenue,
    hideVenueLogo: !isMultiVenueProject,
    newShopConfig: pageConfig?.newShop,
  })

  // Search Result Ads
  const sponsoredContentListItems = useMemo(
    () => searchResults.map(resultParser),
    [searchResults, resultParser]
  )
  //every xxx results has 1 sponsored-content
  const sponsoredContentFrequency = useMemo(
    () => _.get(searchResultConfig, "frequency", 2),
    [searchResultConfig]
  )

  const searchResultItems = useMemo(() => {
    const sanitizedVenueFilter =
      venueFilter === DEFAULT_VENUE_FILTER ? null : venueFilter

    //combine search result with Ads when searchValue isn't empty
    if (!_.isEmpty(searchValue)) {
      const results = _(data?.pages)
        .map("data")
        .flatten()
        .map(resultParser)
        .value()

      // frequency <= 0 means "pin all sponsored content to the top"
      if (sponsoredContentFrequency <= 0) {
        return [...sponsoredContentListItems, ...results]
      }

      return _(results)
        .chunk(sponsoredContentFrequency)
        .flatMap((chunk, index) =>
          sponsoredContentListItems[index]
            ? chunk.concat(sponsoredContentListItems[index])
            : chunk
        )
        .value()
    }
    return getSuggestedSearchItems(sanitizedVenueFilter).map(resultParser)
  }, [
    data,
    searchValue,
    getSuggestedSearchItems,
    resultParser,
    venueFilter,
    sponsoredContentListItems,
    sponsoredContentFrequency,
  ])

  // Events
  const onChange = useCallback(
    (event) => {
      const newValue = event.target.value
      if (searchInputRef.current)
        (searchInputRef.current as HTMLInputElement).value = newValue
      debounceSearchOnChange(newValue)
    },
    [debounceSearchOnChange]
  )

  const onChangeKeyboard = useCallback(
    (value) => {
      if (searchInputRef.current)
        (searchInputRef.current as HTMLInputElement).value = value
      debounceSearchOnChange(value)
    },
    [debounceSearchOnChange]
  )

  const clearSearchValue = useCallback(() => {
    if (searchInputRef.current)
      (searchInputRef.current as HTMLInputElement).value = ""
    if (keyboardRef.current) (keyboardRef.current as any).clearInput()

    // A pending debounced write from prior typing would otherwise fire after
    // this immediate clear and resurrect the stale value.
    debounceSearchOnChange.cancel()
    setSearchValue("")
  }, [setSearchValue, debounceSearchOnChange])

  const onClickClearSearchValue = useCallback(() => {
    gtmPushDataLayerSearchKeyword(searchValue, GTM_NO_RESULT_VALUE)
    clearSearchValue()
  }, [clearSearchValue, searchValue])

  // Filter occupants with an available anchor
  const availableOccupants = useMemo(() => {
    return venueOccupant.filter(({ properties }) => _.get(properties, "anchor"))
  }, [venueOccupant])

  const parentCategoryWithOccupant = useMemo(
    () =>
      _(availableOccupants)
        .map("properties.local_parent_categories")
        .compact()
        .flatten()
        .uniqBy("id")
        .value(),
    [availableOccupants]
  )
  const sortedParentCategoryWithOccupant = useMemo(
    () =>
      _.orderBy(parentCategoryWithOccupant, [
        "properties.is_featured",
        "properties.order",
        ["desc", "asc"],
      ]),
    [parentCategoryWithOccupant]
  )

  const reset = useCallback(() => {
    clearSearchValue()
    closeSearch()
  }, [closeSearch, clearSearchValue])

  const onClose = useCallback(() => {
    gtmPushDataLayerSearchKeyword(searchValue, GTM_NO_RESULT_VALUE)
    reset()
  }, [searchValue, reset])

  const onSelectResult = useCallback(
    (feature) => {
      if (feature) {
        gtmPushDataLayerSearchKeyword(
          searchValue,
          _.get(feature, "properties.name.en")
        )
      }
      reset()
      navigate(`/maps/place/${feature.id}`)
    },
    [navigate, reset, searchValue]
  )

  const onClickCategory = useCallback(
    (category) => {
      const categoryName = category?.properties?.name?.en
      if (searchInputRef.current)
        (searchInputRef.current as HTMLInputElement).value = categoryName
      gtmPushDataLayerCategoryQuickSearch(categoryName)
      // Same reason as clearSearchValue: cancel any pending debounced write
      // from prior typing before this immediate one.
      debounceSearchOnChange.cancel()
      setSearchValue(categoryName)
    },
    [setSearchValue, debounceSearchOnChange]
  )

  const onClickSuggestedItem = useCallback(
    (_splide, { slide }, _event) => {
      reset()
      navigate(slide.getAttribute("link"))
    },
    [navigate, reset]
  )

  // Effect
  // `reset` changes identity on every URL change, so keep it in a ref and
  // register the listener once — otherwise each keystroke leaks a listener that
  // is never removed (kiosks never reload, so they accumulate for the whole
  // uptime and every "appreset" runs reset() once per leaked listener).
  const resetRef = useRef(reset)
  resetRef.current = reset

  useEffect(() => {
    const handler = () => resetRef.current()
    window.addEventListener("appreset", handler)
    return () => window.removeEventListener("appreset", handler)
  }, [])

  useEffect(() => {
    return () => debounceSearchOnChange.cancel()
  }, [debounceSearchOnChange])

  return {
    // Input ref
    searchInputRef,
    keyboardRef,
    endScrollRef,
    // States
    searchResultItems,
    suggestedListItems: suggestedBrands,
    searchValue,
    openSearchState,
    showOnscreenKeyboard,
    venueFilter,
    sortedParentCategoryWithOccupant,
    hasNextPage,
    keyboardLangs: keyboardConfig?.languages ?? ["en"],
    // Functions
    openSearch,
    setSearchValue,
    onSelectResult,
    onChange,
    onChangeKeyboard,
    onClickCategory,
    reset,
    onClose,
    clearSearchValue,
    onClickClearSearchValue,
    setVenueFilter,
    onClickSuggestedItem,
  }
}
